import { defineStore } from 'pinia';
import { computed, ref } from 'vue';

import type { ActivityDetailsData } from '@apps/main/new-desktop/modules/activityDetails';
import { useProductDetailStore } from '@apps/main/new-desktop/core/stores/productDetail/productDetailStore';
import {
  useItineraryStore,
  useTripStore,
} from '@apps/main/new-desktop/core/stores';

import type {
  DayTicketRate,
  DetailsStaticInfo,
  IngredientId,
} from '@libs/trip-planner-interfaces';
import { ProductSearchType } from '@libs/trip-planner-interfaces';

import type {
  ActivitySelectionActivityDetailsPanel,
  ActivitySelectionPanel,
} from '../models';
import {
  ACTIVITY_SELECTION_NO_PANEL,
  ActivitySelectionPanelType,
} from '../models';

export const useActivitySelectionPanelsStore = defineStore(
  'ActivitySelectionPanelsStore',
  () => {
    const productDetailStore = useProductDetailStore();
    const tripStore = useTripStore();
    const itineraryStore = useItineraryStore();

    // State
    const activitySelectionPanel = ref<ActivitySelectionPanel>(
      ACTIVITY_SELECTION_NO_PANEL
    );

    // Actions
    function openActivityDetailsPanel(
      productCode: string,
      ingredientId: IngredientId
    ): void {
      const activityDetailsPanel: ActivitySelectionActivityDetailsPanel = {
        type: ActivitySelectionPanelType.ActivityDetails,
        hotelCode: productCode,
        ingredientId,
      };
      activitySelectionPanel.value = activityDetailsPanel;
      productDetailStore.selectDestinationProduct(
        productCode,
        ingredientId,
        ProductSearchType.Activity
      );
    }

    function closeActivityDetailsPanel(): void {
      activitySelectionPanel.value = ACTIVITY_SELECTION_NO_PANEL;
      productDetailStore.clearProductDetails();
    }

    // Getters
    const activitySelectionPanelType = computed<ActivitySelectionPanelType>(
      () => activitySelectionPanel.value.type
    );

    const activityDetailsData = computed<ActivityDetailsData | undefined>(
      () => {
        const destinationProductDetail =
          productDetailStore.destinationProductDetail;

        if (
          activitySelectionPanel.value.type !==
            ActivitySelectionPanelType.ActivityDetails ||
          !destinationProductDetail
        ) {
          return;
        }

        const ingredientDate = itineraryStore.getIngredientDate(
          destinationProductDetail.staticInfo.ingredientId
        );
        if (!ingredientDate) {
          return;
        }

        const destination = productDetailStore.isDetailStaticInfo(
          destinationProductDetail.staticInfo
        )
          ? tripStore.getMainPolygonFullName(
              destinationProductDetail.parentIngredientId,
              destinationProductDetail.staticInfo
            )
          : undefined;

        return {
          parentIngredientId: destinationProductDetail.parentIngredientId,
          productCode: destinationProductDetail.productCode,
          staticInfo: destinationProductDetail.staticInfo as DetailsStaticInfo,
          isHearted: destinationProductDetail.isHearted,
          destination,
          numberOfNights: destinationProductDetail.numberOfNights,
          availability:
            destinationProductDetail.availability as DayTicketRate[],
          ingredientDate,
          isAvailabilityLoading: destinationProductDetail.isAvailabilityLoading,
        };
      }
    );

    return {
      // Actions
      openActivityDetailsPanel,
      closeActivityDetailsPanel,
      // Getters
      activitySelectionPanelType,
      activityDetailsData,
    };
  }
);
